import type { MouseEvent } from 'react';
import type { RestDataResponseTrialEligibilityResponse } from '@cecm-microservices-sdk/core/services/trial/used-car-eligibility-check';
import { buildCareRouteUrl, getCareBaseUrl } from '@/services/careUrls';
import { getTrialActivationBaseUrl as getConfiguredTrialActivationBaseUrl } from '@/services/runtimeConfig';

type TrialEligibilityResponse = RestDataResponseTrialEligibilityResponse['data'];
type TrialEligibilityApiResponse = RestDataResponseTrialEligibilityResponse & {
    error?: {
        errorCode?: string;
    };
};

export type RflzResponseHandlingResult =
    | { type: 'redirect'; url: string }
    | { type: 'message'; message: string | React.JSX.Element }
    | { type: 'none' };

const RFLZ_ERROR_MESSAGES: Record<string, string | React.JSX.Element> = {
    '3494': 'Please enter a valid VIN or Radio ID.',
};

const RFLZ_INTCMP_VALUE = 'Hero_2_0_NA_www:trial-eligibility_Checkmyeligibility_Hero20';

function appendIntcmpParam(url: string): string {
    try {
        const resolvedUrl = new URL(url, window.location.origin);
        resolvedUrl.searchParams.set('intcmp', RFLZ_INTCMP_VALUE);
        return resolvedUrl.toString();
    } catch {
        return url;
    }
}

const RFLZ_REDIRECTS: Record<string, string> = {
    '101': 'https://www.siriusxm.com/NOUVSATOFFER?errorCode=101',
    '109': 'https://www.siriusxm.com/NOUVSATOFFER?errorCode=109',
    '5035': 'https://www.siriusxm.com/NOUVSTROFFER?errorCode=5035',
    '103': 'https://www.siriusxm.com/NOUVSTROFFER?errorCode=103',
    '111': 'https://www.siriusxm.com/NOUVSATOFFER?errorCode=111',
    '113': 'https://www.siriusxm.com/NOUVSATOFFER?errorCode=113',
    '112': 'https://www.siriusxm.com/NOUVSATOFFER?errorCode=112',
};

export function buildRflzSystemDownMessage(): React.JSX.Element {
    return buildChatMessage(
        'Unfortunately, our systems are down please try again later. For help ',
        '1-855-241-5969',
        'chat with an agent',
    );
}

function buildRflzOfflineActivationMessage(): React.JSX.Element {
    return buildChatMessage(
        "We're sorry for the inconvenience, but this radio can't be activated online. ",
        '1-888-601-6271',
        'Chat with an agent',
        { underlineChatText: true },
    );
}

function getTrialActivationBaseUrl(): string | undefined {
    const trialBaseUrl = getConfiguredTrialActivationBaseUrl();

    if (!trialBaseUrl) {
        return undefined;
    }

    return trialBaseUrl.replace(/\/+$/, '');
}

function buildRefreshAndLoginMessage(messagePrefix: string, radioIdOrVIN: string): React.JSX.Element {
    const careBaseUrl = getCareBaseUrl();
    const refreshRadioUrl = careBaseUrl
        ? buildCareRouteUrl('refreshRadio', {
              queryParams: {
                  receiver: radioIdOrVIN,
              },
          })
        : undefined;
    const loginUrl = careBaseUrl ? buildCareRouteUrl('loginView') : undefined;

    return (
        <>
            {messagePrefix}
            {refreshRadioUrl ? (
                <a href={refreshRadioUrl} target="_blank" rel="noreferrer">
                    refreshing your radio
                </a>
            ) : (
                'refreshing your radio'
            )}
            {'. Or, '}
            {loginUrl ? (
                <a href={loginUrl} target="_blank" rel="noreferrer">
                    log in
                </a>
            ) : (
                'log in'
            )}
            {' to update/manage your account.'}
        </>
    );
}

function openChat(e: MouseEvent<HTMLElement>) {
    e.preventDefault();
    const clientWindow = window as BrowserWindow;
    clientWindow?.sierraSXM?.openChatModal();
}

function sanitizePhoneNumber(phoneNumber: string): string {
    return phoneNumber.replace(/[^\d+]/g, '');
}

function buildChatMessage(
    messagePrefix: string,
    phoneNumber: string,
    chatText = 'Chat with an agent',
    options?: {
        underlineChatText?: boolean;
    },
): React.JSX.Element {
    return (
        <>
            {messagePrefix}
            <button
                type="button"
                onClick={openChat}
                style={{
                    padding: 0,
                    border: 0,
                    background: 'none',
                    color: 'inherit',
                    font: 'inherit',
                    lineHeight: 'inherit',
                    cursor: 'pointer',
                    textDecoration: options?.underlineChatText ? 'underline' : 'none',
                }}
            >
                {chatText}
            </button>
            {', or call us at '}
            <a href={`tel:${sanitizePhoneNumber(phoneNumber)}`}>{phoneNumber}</a>
            {' and let us help.'}
        </>
    );
}

function getTeslaRedirectUrl(responseData: TrialEligibilityResponse, radioIdOrVIN?: string): string | undefined {
    if (responseData.deviceStatus !== 'TESLA') {
        return undefined;
    }

    const careBaseUrl = getCareBaseUrl();

    if (!careBaseUrl) {
        return undefined;
    }

    const lifecycleState = responseData.atlasVehicleDeviceState?.lifecycleState;
    const lifecycleStateType = lifecycleState?.type;
    const deviceId = responseData.atlasVehicleDeviceState?.deviceId;
    const billingSystem = responseData.atlasVehicleDeviceState?.billingSystem;
    const radioId = responseData.last4DigitsOfRadioId?.trim();

    if (
        (lifecycleStateType === 'demo' ||
            lifecycleStateType === 'trialEligible' ||
            lifecycleStateType === 'inactive') &&
        billingSystem === 'sms' &&
        radioId
    ) {
        return buildCareRouteUrl('targetedOemTrial', {
            queryParams: {
                salesRedirect: 'true',
                radioId,
            },
        });
    } else if ((lifecycleStateType === 'trialEligible' || lifecycleStateType === 'demo') && deviceId) {
        return buildCareRouteUrl('teslaSubscribe', {
            queryParams: {
                promoCode: '5FOR12',
                vehicleMake: 'Tesla',
                deviceId,
            },
        });
    } else if (lifecycleStateType === 'trial' && lifecycleState?.phaseInfo === 'anonymous' && deviceId) {
        return `/teslaoffer?deviceId=${encodeURIComponent(deviceId)}`;
    } else if (
        (lifecycleStateType === 'inactive' ||
            (lifecycleStateType === 'trial' && lifecycleState?.phaseInfo === 'claimed')) &&
        deviceId
    ) {
        return `/teslaoffer1?deviceId=${encodeURIComponent(deviceId)}`;
    }

    return undefined;
}

function buildStandardSuccessRedirectUrl(responseData: TrialEligibilityResponse): string | undefined {
    const trialActivationBaseUrl = getTrialActivationBaseUrl();

    if (
        responseData.status !== 'SUCCESS' ||
        !responseData.last4DigitsOfRadioId ||
        !responseData.usedCarBrandingType ||
        typeof window === 'undefined' ||
        !trialActivationBaseUrl
    ) {
        return undefined;
    }

    const currentUrl = new URL(window.location.href);
    const redirectUrl = new URL(trialActivationBaseUrl, currentUrl.origin);
    const currentParams = currentUrl.searchParams;
    const redirectParams = redirectUrl.searchParams;

    redirectParams.set('radioId', responseData.last4DigitsOfRadioId);
    redirectParams.set('usedCarBrandingType', responseData.usedCarBrandingType);
    redirectParams.set('redirectUrl', window.location.href);

    const programCode = currentParams.get('programCode') || currentParams.get('programcode');
    if (programCode) {
        redirectParams.set('programCode', programCode);
    }

    const langpref = currentParams.get('langpref');
    if (langpref) {
        redirectParams.set('langpref', langpref);
    }

    currentParams.forEach((value, key) => {
        if (key.startsWith('utm_')) {
            redirectParams.set(key, value);
        }
    });

    const roktId = currentParams.get('rokt_id');
    if (roktId) {
        redirectParams.set('rokt_id', roktId);
    }

    const contestId = currentParams.get('contestId');
    const contestUrl = currentParams.get('contestUrl');
    if (contestId && contestUrl) {
        redirectParams.set('contestId', contestId);
        redirectParams.set('contestUrl', contestUrl);
    }

    return redirectUrl.toString();
}

function buildTargetedRedirectUrl(
    responseData: TrialEligibilityResponse,
    fallbackRadioId?: string,
): string | undefined {
    const trialActivationBaseUrl = getTrialActivationBaseUrl();

    if (typeof window === 'undefined' || !trialActivationBaseUrl) {
        return undefined;
    }

    const redirectUrl = new URL(`${trialActivationBaseUrl}/targeted`);
    const normalizedFallbackRadioId = fallbackRadioId?.trim().slice(-4);
    const radioId = responseData.last4DigitsOfRadioId || normalizedFallbackRadioId;
    const usedCarBrandingType = responseData.usedCarBrandingType || 'DEMO_USED_CAR';

    if (!radioId) {
        return undefined;
    }

    redirectUrl.searchParams.set('radioId', radioId);
    redirectUrl.searchParams.set('usedCarBrandingType', usedCarBrandingType);
    redirectUrl.searchParams.set('redirectUrl', window.location.href);

    return redirectUrl.toString();
}

export function handleRflzResponse(
    response: TrialEligibilityApiResponse,
    radioIdOrVIN: string,
): RflzResponseHandlingResult {
    const browserWindow = window as BrowserWindow;

    if (response.status !== 'SUCCESS') {
        browserWindow.pushToAdobe({
            event: 'system-error',
            errorInfo: [
                {
                    errorType: 'SYSTEM',
                    errorName: 'System down',
                    errorCode: '',
                },
            ],
        });

        return {
            type: 'message',
            message: buildRflzSystemDownMessage(),
        };
    }

    if (response.error?.errorCode === 'GENERIC') {
        browserWindow.pushToAdobe({
            event: 'system-error',
            errorInfo: [
                {
                    errorType: 'SYSTEM',
                    errorName: 'System down',
                    errorCode: '',
                },
            ],
        });

        return {
            type: 'message',
            message: buildRflzSystemDownMessage(),
        };
    }

    const responseData = response.data;
    if (!responseData) {
        return { type: 'none' };
    }

    const teslaRedirectUrl = getTeslaRedirectUrl(responseData, radioIdOrVIN);
    if (teslaRedirectUrl) {
        return { type: 'redirect', url: appendIntcmpParam(teslaRedirectUrl) };
    }

    const standardSuccessRedirectUrl = buildStandardSuccessRedirectUrl(responseData);
    if (standardSuccessRedirectUrl) {
        return { type: 'redirect', url: appendIntcmpParam(standardSuccessRedirectUrl) };
    }

    const errorCode = responseData.errorCode;
    if (!errorCode) {
        return { type: 'none' };
    }

    const redirectUrl = RFLZ_REDIRECTS[errorCode];
    if (redirectUrl) {
        return { type: 'redirect', url: appendIntcmpParam(redirectUrl) };
    }

    if (errorCode === '110') {
        const errorMsg =
            'Looks like you already have an active trial and your paid subscription will start once your trial is over.';

        browserWindow.pushToAdobe({
            event: 'business-error',
            errorInfo: [
                {
                    errorType: 'BUSINESS',
                    errorName: errorMsg,
                    errorCode: '110',
                },
            ],
        });

        return {
            type: 'message',
            message: buildRefreshAndLoginMessage(`${errorMsg} Try `, radioIdOrVIN),
        };
    }

    if (errorCode === '107') {
        const errorMsg = 'Looks like there’s already a paid subscription associated with this radio.';

        browserWindow.pushToAdobe({
            event: 'business-error',
            errorInfo: [
                {
                    errorType: 'BUSINESS',
                    errorName: errorMsg,
                    errorCode: '107',
                },
            ],
        });

        return {
            type: 'message',
            message: buildRefreshAndLoginMessage(`${errorMsg} Try `, radioIdOrVIN),
        };
    }

    if (errorCode === '114') {
        const targetedRedirectUrl = buildTargetedRedirectUrl(responseData, radioIdOrVIN);

        browserWindow.pushToAdobe({
            event: 'business-error',
            errorInfo: [
                {
                    errorType: 'BUSINESS',
                    errorName: '',
                    errorCode: '114',
                },
            ],
        });

        if (targetedRedirectUrl) {
            return { type: 'redirect', url: appendIntcmpParam(targetedRedirectUrl) };
        }
    }

    if (errorCode === '115') {
        browserWindow.pushToAdobe({
            event: 'business-error',
            errorInfo: [
                {
                    errorType: 'BUSINESS',
                    errorName: "We're sorry for the inconvenience, but this radio can't be activated online.",
                    errorCode: '115',
                },
            ],
        });

        return {
            type: 'message',
            message: buildRflzOfflineActivationMessage(),
        };
    }

    if (errorCode === '106') {
        browserWindow.pushToAdobe({
            event: 'system-error',
            errorInfo: [
                {
                    errorType: 'SYSTEM',
                    errorName: '',
                    errorCode: '106',
                },
            ],
        });

        return {
            type: 'message',
            message: buildRflzSystemDownMessage(),
        };
    }

    const inlineMessage = RFLZ_ERROR_MESSAGES[errorCode];
    if (inlineMessage) {
        return { type: 'message', message: inlineMessage };
    }

    return { type: 'none' };
}
